// The guest-facing booking flow.
//
// It renders INSIDE the immersive surface (plan §5.5) rather than as a modal:
// the widget lives in a shadow root on customer sites, and a portal-less
// fixed-position modal outside that tree is how you get a panel the host page
// can paint over. Staying inside also keeps the chosen room on screen the
// whole way through, which is the point · the guest never loses the thing
// they are buying.

import { AnimatePresence, motion } from "framer-motion";
import ResponsiveImage from "../templates/utils/ResponsiveImage";
import { formatPrice } from "../templates/utils/showcase";
import localization from "../../../../../assets/localization";
import useBookingState, {
  BookingStep,
  DraftRate,
  GuestField,
} from "./BookingState";
import { mockBookingDriver } from "./bookingDriver";

interface BookingPanelProps {
  language: string;
  isMobile?: boolean;
}

// The dots at the top. "submitting" is not a step the guest chose, so it keeps
// the review dot lit rather than inventing a fourth one.
const STEP_ORDER: BookingStep[] = ["rate", "details", "review"];

// dengine sends ISO dates because that's data; the presentation is ours.
// An unparseable value is shown verbatim rather than hidden · losing the date
// is worse than showing it in the wrong shape.
const formatStayDate = (iso: string | null | undefined, language: string) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "short",
  }).format(date);
};

const positionOf = (step: BookingStep, hasRateChoice: boolean) => {
  if (step === "success" || step === "error") return STEP_ORDER.length;
  if (step === "submitting") return STEP_ORDER.indexOf("review");
  const index = STEP_ORDER.indexOf(step);
  return hasRateChoice ? index : Math.max(0, index - 1);
};

export default function BookingPanel({ language, isMobile }: BookingPanelProps) {
  const t = localization.get(language);
  const booking = useBookingState();
  const draft = booking.draft;

  if (!draft) return null;

  const b = t.booking;
  const form = t.handoff_form;
  const hasRateChoice = draft.rates.length > 1;
  const steps = hasRateChoice ? STEP_ORDER : STEP_ORDER.slice(1);
  const position = positionOf(booking.step, hasRateChoice);
  const isMock = booking.driver === mockBookingDriver;

  const stepLabel = (step: BookingStep) =>
    step === "rate" ? b.choose_rate : step === "details" ? b.your_details : b.review;

  const field = (
    name: GuestField,
    label: string,
    error: string,
    type = "text"
  ) => {
    const invalid = booking.showErrors && !!booking.errors[name];
    return (
      <label className="booking-field">
        <span className="booking-field-label">{label}</span>
        <input
          className={`booking-input ${invalid ? "invalid" : ""}`}
          type={type}
          value={draft.guest[name]}
          aria-invalid={invalid}
          autoComplete={
            name === "firstName"
              ? "given-name"
              : name === "lastName"
              ? "family-name"
              : name === "email"
              ? "email"
              : name === "phone"
              ? "tel"
              : "off"
          }
          onChange={(e) => booking.setGuestField(name, e.target.value)}
        />
        {invalid && <span className="booking-field-error">{error}</span>}
      </label>
    );
  };

  // Everything below is arithmetic on numbers we already have · no claim about
  // the property, nothing invented. A guest choosing between €286 and €446
  // reads three absolute prices and has to do the subtraction themselves; doing
  // it for them is what makes the better board legible instead of expensive.
  const cheapest = draft.rates.length
    ? Math.min(...draft.rates.map((r) => r.price))
    : null;
  const nights = draft.stay.nights ?? null;
  const perNight = (price: number) =>
    nights && nights > 0 ? Math.round(price / nights) : null;

  const renderRates = () => (
    <div className="booking-rates">
      {draft.rates.map((rate: DraftRate, index: number) => {
        const delta = cheapest !== null ? rate.price - cheapest : 0;
        const nightly = perNight(rate.price);
        const selected = draft.rateIndex === index;
        return (
          <button
            key={rate.id ?? index}
            className={`booking-rate ${selected ? "active" : ""}`}
            onClick={() => booking.chooseRate(rate, index)}
            aria-pressed={selected}
          >
            {/* A chosen rate has to read as chosen before the guest looks for
                the price again. The border alone was not saying it. */}
            <span
              className="booking-rate-check"
              aria-hidden="true"
            >
              <i className="fa-solid fa-check"></i>
            </span>
            <span className="booking-rate-main">
              <span className="booking-rate-title">{rate.title}</span>
              {delta === 0 && draft.rates.length > 1 && (
                <span className="booking-rate-badge">{b.cheapest}</span>
              )}
              {rate.conditions && (
                <span className="booking-rate-conditions">
                  {rate.conditions}
                </span>
              )}
            </span>
            <span className="booking-rate-figures">
              <span className="booking-rate-price">
                {formatPrice(rate.price, language)}
              </span>
              <span className="booking-rate-sub">
                {delta > 0 ? `+${formatPrice(delta, language)}` : nightly
                  ? `${formatPrice(nightly, language)} ${b.per_night}`
                  : ""}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderDetails = () => (
    <div className="booking-form">
      <div className="booking-field-row">
        {field("firstName", form.firstname, form.firstname_error)}
        {field("lastName", form.lastname, form.lastname_error)}
      </div>
      {field("email", form.email, form.email_error, "email")}
      {field("phone", form.phone, form.phone_error, "tel")}
      <label className="booking-field">
        <span className="booking-field-label">{b.notes}</span>
        <textarea
          className="booking-input booking-textarea"
          rows={2}
          placeholder={b.notes_placeholder}
          value={draft.guest.notes}
          onChange={(e) => booking.setGuestField("notes", e.target.value)}
        />
      </label>
    </div>
  );

  const renderReview = () => (
    <div className="booking-review">
      <div className="booking-review-row">
        <span>{form.firstname}</span>
        <strong>
          {draft.guest.firstName} {draft.guest.lastName}
        </strong>
      </div>
      <div className="booking-review-row">
        <span>{form.email}</span>
        <strong>{draft.guest.email}</strong>
      </div>
      <div className="booking-review-row">
        <span>{form.phone}</span>
        <strong>{draft.guest.phone}</strong>
      </div>
      {draft.guest.notes.trim() && (
        <div className="booking-review-row">
          <span>{b.notes}</span>
          <strong>{draft.guest.notes}</strong>
        </div>
      )}

      <label className="booking-consent">
        <input
          type="checkbox"
          checked={draft.consent.privacy}
          onChange={(e) => booking.setConsent("privacy", e.target.checked)}
        />
        <span>{b.privacy}</span>
      </label>
      <label className="booking-consent">
        <input
          type="checkbox"
          checked={draft.consent.marketing}
          onChange={(e) => booking.setConsent("marketing", e.target.checked)}
        />
        <span>{b.marketing}</span>
      </label>
    </div>
  );

  const renderSuccess = () => (
    <div className="booking-outcome">
      <div className="booking-outcome-icon success">
        <i className="fa-solid fa-check"></i>
      </div>
      <div className="booking-outcome-title">{b.success_title}</div>
      <div className="booking-outcome-body">{b.success_body}</div>
      {booking.reference && (
        <div className="booking-reference">
          <span>{b.reference}</span>
          <strong>{booking.reference}</strong>
        </div>
      )}
      {isMock && <div className="booking-mock-notice">{b.mock_notice}</div>}
    </div>
  );

  const renderError = () => (
    <div className="booking-outcome">
      <div className="booking-outcome-icon error">
        <i className="fa-solid fa-triangle-exclamation"></i>
      </div>
      <div className="booking-outcome-title">{b.error_title}</div>
      <div className="booking-outcome-body">{b.error_body}</div>
    </div>
  );

  const renderStep = () => {
    switch (booking.step) {
      case "rate":
        return renderRates();
      case "details":
        return renderDetails();
      case "review":
      case "submitting":
        return renderReview();
      case "success":
        return renderSuccess();
      case "error":
        return renderError();
    }
  };

  const renderActions = () => {
    if (booking.step === "success")
      return (
        <button className="showcase-cta" onClick={booking.cancel}>
          {b.close}
        </button>
      );

    if (booking.step === "error")
      return (
        <>
          <button className="showcase-cta secondary" onClick={booking.cancel}>
            {b.close}
          </button>
          <button className="showcase-cta" onClick={booking.submit}>
            {b.retry}
          </button>
        </>
      );

    if (booking.step === "rate") return null;

    const submitting = booking.step === "submitting";
    const blocked = booking.step === "review" && !draft.consent.privacy;

    return (
      <>
        <button
          className="showcase-cta secondary"
          onClick={booking.back}
          disabled={submitting}
        >
          {b.back}
        </button>
        {booking.step === "details" ? (
          <button className="showcase-cta" onClick={booking.goToReview}>
            {b.continue}
          </button>
        ) : (
          <button
            className="showcase-cta"
            onClick={booking.submit}
            disabled={submitting || blocked}
            title={blocked ? b.privacy_required : undefined}
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin"></i>
                {b.submitting}
              </>
            ) : (
              b.confirm
            )}
          </button>
        )}
      </>
    );
  };

  const stay = draft.stay;
  const checkin = formatStayDate(stay.checkin, language);
  const checkout = formatStayDate(stay.checkout, language);
  const stayChips = [
    checkin && checkout ? `${checkin} → ${checkout}` : null,
    stay.nights ? `${stay.nights} ${b.nights}` : null,
    stay.adults ? `${stay.adults + (stay.children ?? 0)} ${b.guests}` : null,
  ].filter(Boolean) as string[];

  return (
    <div className={`booking-panel ${isMobile ? "mobile" : ""}`}>
      <aside className="booking-summary">
        {draft.imageUrl && (
          <div className="booking-summary-hero">
            <ResponsiveImage src={draft.imageUrl} alt={draft.roomTitle} />
          </div>
        )}
        <div className="booking-summary-body">
          <div className="booking-summary-room">{draft.roomTitle}</div>
          {draft.rate && (
            <div className="booking-summary-rate">
              {draft.rate.title}
              {draft.rate.conditions && (
                <span className="booking-summary-conditions">
                  {draft.rate.conditions}
                </span>
              )}
            </div>
          )}
          {stayChips.length > 0 && (
            <div className="booking-summary-chips">
              {stayChips.map((chip) => (
                <span key={chip} className="booking-chip">
                  {chip}
                </span>
              ))}
            </div>
          )}
          {/* Before a rate is picked the rail was blank where the money goes.
              An anchor ("da €198") is the number the guest is actually
              deciding against. */}
          {!draft.rate && cheapest !== null && (
            <div className="booking-summary-total">
              <span>{t.from_price}</span>
              <strong>{formatPrice(cheapest, language)}</strong>
            </div>
          )}
          {draft.rate && (
            <div className="booking-summary-total">
              <span>{b.total}</span>
              <span className="booking-summary-figures">
                <strong>{formatPrice(draft.rate.price, language)}</strong>
                {perNight(draft.rate.price) !== null && (
                  <small>
                    {formatPrice(perNight(draft.rate.price)!, language)} {b.per_night}
                  </small>
                )}
              </span>
            </div>
          )}
          {/* Desktop only here · on a phone the summary is a compact strip with
              no room, and this is the line that answers the hesitation, so it
              moves into the flow instead of disappearing. */}
          {!isMobile && !booking.driver.collectsPayment && (
            <div className="booking-reassurance">
              <i className="fa-solid fa-shield-halved"></i>
              <span>{b.no_payment_now}</span>
            </div>
          )}
        </div>
      </aside>

      <section className="booking-flow">
        <header className="booking-flow-head">
          <div className="booking-steps">
            {steps.map((step, index) => (
              <div
                key={step}
                className={`booking-step ${index === position ? "active" : ""} ${
                  index < position ? "done" : ""
                }`}
              >
                <span className="booking-step-dot">
                  {index < position ? <i className="fa-solid fa-check"></i> : index + 1}
                </span>
                <span className="booking-step-label">{stepLabel(step)}</span>
              </div>
            ))}
          </div>
          <button
            className="booking-cancel"
            onClick={booking.cancel}
            aria-label={b.cancel}
          >
            <i className="fa-solid fa-x"></i>
          </button>
        </header>

        {/* The dots say where you are; this says how much is left. That is the
            question that makes people abandon a form, and it costs 2px. */}
        <div
          className="booking-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={steps.length}
          aria-valuenow={Math.min(position, steps.length)}
        >
          <div
            className="booking-progress-fill"
            style={{
              width: `${(Math.min(position, steps.length) / steps.length) * 100}%`,
            }}
          />
        </div>

        <div className="booking-flow-body">
          {isMobile && !booking.driver.collectsPayment &&
            booking.step !== "success" && booking.step !== "error" && (
              <div className="booking-reassurance mobile">
                <i className="fa-solid fa-shield-halved"></i>
                <span>{b.no_payment_now}</span>
              </div>
            )}

          <AnimatePresence mode="wait">
            <motion.div
              key={booking.step}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8, transition: { duration: 0.18 } }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>

        <footer className="booking-flow-actions">{renderActions()}</footer>
      </section>
    </div>
  );
}
